import { useState } from "react";
import { Pencil, Trash2 } from "lucide-react";
import { useDataAdapter } from "@shared-core/data/DataAdapterContext";
import type {
  CampaignCost,
  CampaignCostType,
  CampaignPerformance,
} from "@shared-core/data/marketing-types";
import { extractFieldErrors } from "@shared-core/data/validation";
import { newUuid } from "@shared-core/domain/object-ui/command-run";
import {
  COST_TYPE_LABELS,
  campaignCostTotal,
  performanceSendLabel,
} from "@shared-core/domain/campaign-performance";
import {
  formatCurrencyGbp,
  formatDateOrDash,
} from "@shared-core/domain/formatters";
import { Button } from "../../primitives/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../primitives/dialog";
import { Input } from "../../primitives/input";
import { Label } from "../../primitives/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../primitives/select";

const WHOLE_CAMPAIGN = "whole-campaign";

interface CostForm {
  costId: string | null;
  /** One per add-cost form, so a double click records one cost. */
  requestId: string;
  costType: CampaignCostType;
  amount: string;
  note: string;
  stepId: string;
}

function emptyForm(): CostForm {
  return {
    costId: null,
    requestId: newUuid(),
    costType: "marketing",
    amount: "",
    note: "",
    stepId: WHOLE_CAMPAIGN,
  };
}

/** The form's inputs, by the request field the backend names in an error. */
type CostField = "amount" | "step_id" | "note";

interface CampaignCostsDialogProps {
  open: boolean;
  /** Kept while the dialog closes, so its content doesn't blank mid-animation. */
  campaign: CampaignPerformance | null;
  canManage: boolean;
  onOpenChange: (open: boolean) => void;
  /** Called after any change so the Performance tab reloads its figures. */
  onChanged: () => void;
}

/**
 * A campaign's costs: the list, with add, edit and delete for the marketing
 * manager. A cost goes against the whole campaign unless it's tied to one
 * send; untied costs count towards the campaign's ROI only.
 */
export function CampaignCostsDialog({
  open,
  campaign,
  canManage,
  onOpenChange,
  onChanged,
}: CampaignCostsDialogProps) {
  const dataAdapter = useDataAdapter();
  const [form, setForm] = useState<CostForm | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<
    Partial<Record<CostField, string>>
  >({});

  const costs = campaign?.costs ?? [];
  const sends = campaign?.sends ?? [];
  const sendLabel = (stepId: string | null) => {
    if (stepId === null) return "Whole campaign";
    const send = sends.find((s) => s.step_id === stepId);
    return send ? performanceSendLabel(send, sends) : "A send";
  };

  function resetErrors() {
    setError(null);
    setFieldErrors({});
  }

  function close(next: boolean) {
    if (!next) {
      setForm(null);
      resetErrors();
    }
    onOpenChange(next);
  }

  function edit(cost: CampaignCost) {
    resetErrors();
    setForm({
      costId: cost.cost_id,
      requestId: newUuid(),
      costType: cost.cost_type,
      amount: String(cost.amount),
      note: cost.note ?? "",
      stepId: cost.step_id ?? WHOLE_CAMPAIGN,
    });
  }

  async function save() {
    if (!campaign || !form) return;
    const amount = Number(form.amount);
    resetErrors();
    if (!Number.isFinite(amount) || amount <= 0) {
      setFieldErrors({ amount: "Enter the cost in pounds, more than £0." });
      setError("1 field needs attention.");
      return;
    }
    const input = {
      cost_type: form.costType,
      amount,
      note: form.note.trim() || null,
      step_id: form.stepId === WHOLE_CAMPAIGN ? null : form.stepId,
    };
    setSaving(true);
    try {
      if (form.costId) {
        await dataAdapter.updateCampaignCost(
          campaign.campaign_id,
          form.costId,
          input,
        );
      } else {
        await dataAdapter.addCampaignCost(campaign.campaign_id, {
          ...input,
          request_id: form.requestId,
        });
      }
      setForm(null);
      onChanged();
    } catch (e) {
      console.error("marketing.performance.cost_save.failed", e);
      const errors = extractFieldErrors(e);
      if (errors.length > 0) {
        setFieldErrors(
          Object.fromEntries(errors.map((f) => [f.field, f.message])),
        );
        setError(
          `${errors.length} field${errors.length === 1 ? "" : "s"} need${
            errors.length === 1 ? "s" : ""
          } attention.`,
        );
      } else {
        setError("Couldn't save this cost. Please try again.");
      }
    } finally {
      setSaving(false);
    }
  }

  async function remove(cost: CampaignCost) {
    if (!campaign) return;
    setSaving(true);
    resetErrors();
    try {
      await dataAdapter.deleteCampaignCost(campaign.campaign_id, cost.cost_id);
      onChanged();
    } catch (e) {
      console.error("marketing.performance.cost_delete.failed", e);
      setError("Couldn't delete this cost. Please try again.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Costs: {campaign?.campaign_name}</DialogTitle>
          <DialogDescription>
            Costs count towards the campaign's cost per lead and ROI. Tie a cost
            to one send to give that send its own ROI too.
          </DialogDescription>
        </DialogHeader>

        {costs.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            No costs entered yet, so cost per lead and ROI show "—".
          </p>
        ) : (
          <ul className="divide-y divide-border rounded-md border border-border">
            {costs.map((cost) => (
              <li
                key={cost.cost_id}
                className="flex items-start justify-between gap-3 px-3 py-2"
              >
                <div className="min-w-0">
                  <div className="text-sm font-medium">
                    {COST_TYPE_LABELS[cost.cost_type]}:{" "}
                    {formatCurrencyGbp(cost.amount, 2)}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {sendLabel(cost.step_id)}
                    {cost.note ? ` · ${cost.note}` : ""}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    Entered by {cost.entered_by},{" "}
                    {formatDateOrDash(cost.entered_at)}
                  </div>
                </div>
                {canManage && (
                  <div className="flex shrink-0 gap-1">
                    <Button
                      variant="ghost"
                      size="icon"
                      aria-label="Edit cost"
                      onClick={() => edit(cost)}
                      disabled={saving}
                    >
                      <Pencil className="size-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      aria-label="Delete cost"
                      onClick={() => void remove(cost)}
                      disabled={saving}
                    >
                      <Trash2 className="size-4" />
                    </Button>
                  </div>
                )}
              </li>
            ))}
            <li className="flex justify-between px-3 py-2 text-sm font-medium">
              <span>Total</span>
              <span>{formatCurrencyGbp(campaignCostTotal(costs), 2)}</span>
            </li>
          </ul>
        )}

        {canManage && form && (
          <div className="space-y-3 rounded-md border border-border p-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label htmlFor="cost-type">Type</Label>
                <Select
                  value={form.costType}
                  onValueChange={(v) =>
                    setForm({ ...form, costType: v as CampaignCostType })
                  }
                >
                  <SelectTrigger id="cost-type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(COST_TYPE_LABELS).map(([type, label]) => (
                      <SelectItem key={type} value={type}>
                        {label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label htmlFor="cost-amount">Amount (£)</Label>
                <Input
                  id="cost-amount"
                  inputMode="decimal"
                  value={form.amount}
                  onChange={(e) => setForm({ ...form, amount: e.target.value })}
                  aria-invalid={fieldErrors.amount !== undefined}
                  aria-describedby={
                    fieldErrors.amount ? "cost-amount-error" : undefined
                  }
                />
                {fieldErrors.amount && (
                  <p
                    id="cost-amount-error"
                    className="text-sm text-red-600 dark:text-red-400"
                  >
                    {fieldErrors.amount}
                  </p>
                )}
              </div>
            </div>
            <div className="space-y-1">
              <Label htmlFor="cost-send">Applies to</Label>
              <Select
                value={form.stepId}
                onValueChange={(v) => setForm({ ...form, stepId: v })}
              >
                <SelectTrigger
                  id="cost-send"
                  aria-invalid={fieldErrors.step_id !== undefined}
                  aria-describedby={
                    fieldErrors.step_id ? "cost-send-error" : undefined
                  }
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={WHOLE_CAMPAIGN}>Whole campaign</SelectItem>
                  {sends.map((send) => (
                    <SelectItem key={send.step_id} value={send.step_id}>
                      {send.plan_ref ? `${send.plan_ref} · ` : ""}
                      {performanceSendLabel(send, sends)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {fieldErrors.step_id && (
                <p
                  id="cost-send-error"
                  className="text-sm text-red-600 dark:text-red-400"
                >
                  {fieldErrors.step_id}
                </p>
              )}
            </div>
            <div className="space-y-1">
              <Label htmlFor="cost-note">Note (optional)</Label>
              <Input
                id="cost-note"
                value={form.note}
                placeholder="e.g. invoice 4471, email template design"
                onChange={(e) => setForm({ ...form, note: e.target.value })}
                aria-invalid={fieldErrors.note !== undefined}
                aria-describedby={
                  fieldErrors.note ? "cost-note-error" : undefined
                }
              />
              {fieldErrors.note && (
                <p
                  id="cost-note-error"
                  className="text-sm text-red-600 dark:text-red-400"
                >
                  {fieldErrors.note}
                </p>
              )}
            </div>
          </div>
        )}

        {error && (
          <p role="alert" className="text-sm text-red-600 dark:text-red-400">
            {error}
          </p>
        )}

        {canManage && (
          <DialogFooter>
            {form ? (
              <>
                <Button
                  variant="outline"
                  onClick={() => setForm(null)}
                  disabled={saving}
                >
                  Cancel
                </Button>
                <Button onClick={() => void save()} disabled={saving}>
                  {form.costId ? "Save cost" : "Add cost"}
                </Button>
              </>
            ) : (
              <Button
                onClick={() => {
                  resetErrors();
                  setForm(emptyForm());
                }}
              >
                Add cost
              </Button>
            )}
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}
